import React, { useEffect, useState } from 'react'

import { DocumentListContainer, DocumentGridModel } from '../../shared/documents/DocumentListContainer'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { GridMenuActionLink, GridMenuCell, GridMenuLink } from '../../shared/grid/GridMenu'
import {
    EInvoiceModel,
    EInvoiceRequestModel,
    InvoiceApi,
    InvoiceConfigurationModel,
    RequestEInvoiceResult,
} from '../../services/InvoiceApi'
import { useNavigate, useLocation } from 'react-router-dom'
import { toastError, toastSuccess, toastWarning } from '../../shared/toastr'
import { GridFilterForm, GridFilterInput } from '../../shared/grid/GridFilter'
import { DocumentDateFilters } from '../../shared/documents/DocumentDateFilters'
import Modal from '../../shared/modal'
import { ConfirmFiscalizationModal } from './ConfirmFiscalizationModal'
import { PaymentMethod, ReceiptStatus } from '../../shared/models'
import { CompanySettingsApi } from '../../services/CompanySettingsApi'
import { SendViaEmailModal } from '../../shared/documents/SendViaEmailModal'
import { ChangeStatusModal } from '../../shared/documents/ChangeStatusModal'
import { CurrencyLabel } from '../../shared/ui/currency'
import dayjs from 'dayjs'
import { Field } from 'formik'
import { EInvoiceModal, EInvoiceModalType } from './EInvoiceModal'
import { useOfferionModal } from '../../shared/modal/ConfirmationModal'
import { GridActions } from '../../shared/grid/Types'
import { AggregatedResultsButton } from '../../shared/documents/details/elements/AggregatedResultsButton'
import { faEdit, faEnvelope, faHistory, faPrint, faStream, faTimes, faTrashAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { useGridActions } from '../../shared/grid/GridContextProvider'
import { openDocument } from '../../shared/utils/openDocument'
import { InvoiceLogModal } from './InvoiceDetailsContainer'
import { useTranslation } from 'react-i18next'
import StatusIndicator from '../ui/StatusIndicator'
import { Checkbox } from '../ui/Checkbox'
import { ClientApi } from '../../services/ClientApi'
import { FiscalizationApi } from '../../services/FiscalizationApi'
import { isLawInEffect } from '../../shared/utils/dates'
import { validateEInvoiceItems, validateEInvoicePreOpenForList } from '../../shared/utils/eInvoicePrechecks'
import { validateOib } from '../../shared/utils/validators'
import { date } from '../../shared/utils/formatters'

interface InvoiceListConfig {
    pageTitleKey: string
    gridTitleKey: string
    buttons: React.ComponentType
    hideFilters?: boolean
    hidePaging?: boolean
    defaultPageSize?: number
    defaultCustomFilter?: string | null
}

//if obrt contains word "obrt" then we treat it as small business, otherwise as physical person (by client request)
function isObrtName(name?: string | null): boolean {
    if (!name) return false

    return /\bobrt\b/i.test(name)
}

function getAutofillFlags(data: { legalFormShort?: string | null }) {
    const legalFormEmpty = !data.legalFormShort || data.legalFormShort.trim() === ''
    return { legalFormEmpty }
}

function shouldFiscalize(row: DocumentGridModel, isFiscalizationActive: boolean) {
    return isFiscalizationActive && !row.isFiscalized && row.paymentMethod !== PaymentMethod.Account
}

function isWithinFiscalWindowFor(officialDate?: string | Date | null) {
    if (!officialDate) return false
    const dayJsOfficialDate = dayjs(officialDate).add(3, 'day').startOf('day')
    const today = dayjs(new Date()).startOf('day')
    return dayJsOfficialDate.isAfter(today)
}

const PrintAction = ({
    action,
    row,
    getDocumentFinal,
}: {
    action: () => Promise<{
        openModal: boolean
        oibDataFound: boolean | null
        initialShouldNotFiscalize: boolean
        showNewFiscalizeMessage: boolean
        numberFormatError: boolean
    }>
    row: { invoiceNumber: string } & DocumentGridModel
    getDocumentFinal: (row: { invoiceNumber: string } & DocumentGridModel, shouldFiscalize: boolean) => Promise<boolean>
}) => {
    const [confirmFiscalizationPopupVisible, setConfirmFiscalizationPopupVisible] = useState(false)
    const [oibDataFound, setOibDataFound] = useState<boolean | null>(null)
    const [initialShouldNotFiscalize, setInitialShouldNotFiscalize] = useState<boolean>(false)
    const [showNewFiscalizeMessage, setShowNewFiscalizeMessage] = useState<boolean>(false)
    const [numberFormatError, setNumberFormatError] = useState<boolean>(false)
    const gridActions = useGridActions()

    return (
        <>
            <GridMenuActionLink
                icon={faPrint}
                onClick={() =>
                    action().then(res => {
                        setOibDataFound(res.oibDataFound)
                        setInitialShouldNotFiscalize(isWithinFiscalWindowFor(row.officialDate) ? res.initialShouldNotFiscalize : true)
                        setConfirmFiscalizationPopupVisible(res.openModal)
                        setShowNewFiscalizeMessage(res.showNewFiscalizeMessage)
                        setNumberFormatError(res.numberFormatError)
                    })
                }
                translationKey="Invoice.Side.print"
            ></GridMenuActionLink>

            <Modal isOpen={confirmFiscalizationPopupVisible}>
                <ConfirmFiscalizationModal
                    officialDate={row.officialDate}
                    clientOib={row.oib}
                    clientName={row.clientName}
                    eInvoiceSent={row.eInvoiceSent}
                    oibDataFound={oibDataFound}
                    initialShouldNotFiscalize={initialShouldNotFiscalize}
                    showNewFiscalizeMessage={showNewFiscalizeMessage}
                    numberFormatError={numberFormatError}
                    close={() => setConfirmFiscalizationPopupVisible(false)}
                    onSubmit={shouldFiscalize =>
                        getDocumentFinal(row, shouldFiscalize)
                            .then(result => setConfirmFiscalizationPopupVisible(result))
                            .then(() => {
                                if (shouldFiscalize) gridActions.refresh()
                            })
                    }
                ></ConfirmFiscalizationModal>
            </Modal>
        </>
    )
}

const EmailAction = ({
    defaultMailText,
    row,
    isFiscalizationActive,
    emailInvoice,
}: {
    defaultMailText: string
    isFiscalizationActive: boolean
    row: { invoiceNumber: string } & DocumentGridModel
    emailInvoice: (id: number, values: any) => Promise<any>
}) => {
    const { t } = useOfferionTranslation()
    const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false)
    const gridActions = useGridActions()
    const [shouldFiscalizeDefault, setShouldFiscalizeDefault] = useState(false)
    const [oibDataFound, setOibDataFound] = useState<boolean | null>(null)
    const [isObrtDetected, setIsObrtDetected] = useState(false)
    const [isLegalFormEmpty, setIsLegalFormEmpty] = useState(false)
    const [isNumberFormatInvalid, setIsNumberFormatInvalid] = useState(false)
    const isAccountPaymentMethod = row.paymentMethod === PaymentMethod.Account
    const isAfterLawChange = isLawInEffect()

    const isWithinFiscalWindow = () => isWithinFiscalWindowFor(row.officialDate)

    const canShowFiscalize = () => {
        if (isNumberFormatInvalid) return false
        if (row.eInvoiceSent) return false
        if (row.isFiscalized || !isFiscalizationActive) return false
        if (!isWithinFiscalWindow()) return false
        if (isAccountPaymentMethod) {
            // account payments priority:
            //  if name contains "obrt", do NOT show
            //  else show when (legalFormShort is empty) OR (OIB not found)
            return !isObrtDetected && (isLegalFormEmpty || oibDataFound === false)
        }
        return true
    }

    const handleOpenModal = async () => {
        let shouldFiscalize = false

        // If already fiscalized or fiscalization not active, just open modal without fiscalize option
        if (row.isFiscalized || !isFiscalizationActive) {
            setOibDataFound(null)
            setIsNumberFormatInvalid(false)
            setShouldFiscalizeDefault(false)
            setSendEmailModalVisible(true)
            return
        }

        // Check if invoice number format matches a registered sale place
        let numberFormatInvalid = false
        try {
            const numCheck = await FiscalizationApi.canFiscalizeByNumber(row.id)
            numberFormatInvalid = !numCheck.isValid
        } catch (e) {
            console.error('Failed to check invoice number for fiscalization (email)', e)
        }
        setIsNumberFormatInvalid(numberFormatInvalid)

        if (isAccountPaymentMethod) {
            // transactional (Account) payments:
            // if name contains "obrt, treat as company-like and do NOT offer fiscalization
            // else, offer fiscalization only when legalFormShort is empty OR OIB is not found
            if (row.oib) {
                try {
                    const res = await ClientApi.getAutofill(row.oib)
                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(row.clientName)
                    setIsObrtDetected(isObrt)
                    setIsLegalFormEmpty(legalFormEmpty)

                    if (res.dataFound) {
                        if (isObrt) {
                            // if "obrt" in name, company-like for Account; do NOT offer
                            setOibDataFound(false)
                        } else if (legalFormEmpty) {
                            // legalFormShort empty, allow fiscalization (checkbox, default within window)
                            setOibDataFound(false)
                            shouldFiscalize = isWithinFiscalWindow()
                        } else {
                            // legalFormShort present (company like)m do NOT offer
                            setOibDataFound(true)
                        }
                    } else {
                        // if OIB not found, allow fiscalization (checkbox, default within window)
                        setOibDataFound(false)
                        setIsObrtDetected(false)
                        setIsLegalFormEmpty(true)
                        shouldFiscalize = isWithinFiscalWindow()
                    }
                } catch (e) {
                    console.error('Failed to check OIB for fiscalization', e)
                    setOibDataFound(false)
                    setIsObrtDetected(false)
                    setIsLegalFormEmpty(true)
                    shouldFiscalize = isWithinFiscalWindow()
                }
            } else {
                // if no OIB, treat as not found
                setOibDataFound(false)
                setIsObrtDetected(false)
                setIsLegalFormEmpty(true)
                shouldFiscalize = true
            }
        } else {
            // CASH / NON-ACCOUNT PAYMENTS
            const oib = row.oib || null

            if (isAfterLawChange && oib) {
                try {
                    const res = await ClientApi.getAutofill(oib)

                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(row.clientName)

                    if (isObrt) {
                        // treat OBRT as company-like in cash, do NOT fiscalize by default, show company info
                        setOibDataFound(!!res.dataFound)
                    } else if (legalFormEmpty) {
                        // unknown legal form: allow fiscalization by default
                        setOibDataFound(false)
                        shouldFiscalize = isWithinFiscalWindow()
                    } else {
                        // croatian company: do NOT fiscalize by default if OIB found; otherwise allow
                        setOibDataFound(res.dataFound)
                        shouldFiscalize = res.dataFound ? false : isWithinFiscalWindow()
                    }
                } catch (e) {
                    console.error('Failed to check OIB for fiscalization (cash)', e)
                    setOibDataFound(false)
                    shouldFiscalize = isWithinFiscalWindow()
                }
            } else {
                shouldFiscalize = isWithinFiscalWindow()
                setOibDataFound(false)
            }
        }

        setShouldFiscalizeDefault(shouldFiscalize)
        setSendEmailModalVisible(true)
    }

    return (
        <>
            <GridMenuActionLink icon={faEnvelope} onClick={handleOpenModal} translationKey="Invoice.Side.sendMail"></GridMenuActionLink>
            <Modal isOpen={sendEmailModalVisible}>
                <SendViaEmailModal
                    pageTitleKey="Invoice.Send.pageTitleInvoice"
                    subTitleKey="Invoice.Send.invoiceAttached"
                    defaultMailText={defaultMailText}
                    initialFiscalizationValue={shouldFiscalizeDefault}
                    clientEmail={row.clientEmail}
                    documentDetails={() => (
                        <span className="invoice-info">
                            <ul>
                                <li>{row.invoiceNumber}</li>
                                <li>{row.clientName}</li>
                                <li>
                                    <CurrencyLabel amount={row.sumOfItemsFullPrice} currency={row.currency} />
                                </li>
                                <li>{row.officialDate ? date(row.officialDate) : null}</li>
                            </ul>
                        </span>
                    )}
                    documentFooterRenderer={() => {
                        if (isNumberFormatInvalid) {
                            return (
                                <div className="info__row" style={{ marginTop: 6, color: 'red' }}>
                                    <small>{t('Invoice.Print.Fiscalisation.invalidNumberFormat')}</small>
                                </div>
                            )
                        }
                        return canShowFiscalize() ? (
                            <>
                                <div className="info__row flex">
                                    <Checkbox name="shouldFiscalize" />
                                    <div className="ml1">
                                        <label htmlFor="shouldFiscalize">{t('Invoice.Send.fiscalizeYesNo')}</label>
                                    </div>
                                </div>

                                {(() => {
                                    if (oibDataFound === false) {
                                        return (
                                            <div className="info__row" style={{ marginTop: 6 }}>
                                                <small>{t('Invoice.Print.Fiscalisation.messageNoOibFound')}</small>
                                            </div>
                                        )
                                    }

                                    if (oibDataFound === true) {
                                        return (
                                            <div className="info__row" style={{ marginTop: 6 }}>
                                                <small>{t('Invoice.Print.Fiscalisation.messageOibCroatianCompany')}</small>
                                            </div>
                                        )
                                    }

                                    return null
                                })()}
                            </>
                        ) : null
                    }}
                    close={() => setSendEmailModalVisible(false)}
                    onSubmit={values =>
                        emailInvoice(row.id, {
                            ...values,
                            shouldFiscalize: !!values.shouldFiscalize && canShowFiscalize(),
                        })
                            .then(() => setSendEmailModalVisible(false))
                            .then(() => {
                                if (values.shouldFiscalize) gridActions.refresh()
                            })
                    }
                />
            </Modal>
        </>
    )
}

const ChangeStatusAction = ({
    invoiceStatusModalVisible,
    setInvoiceStatusModalVisible,
    row,
    onSubmit,
}: {
    invoiceStatusModalVisible: number | null
    setInvoiceStatusModalVisible: (v: number | null) => void
    row: { invoiceNumber: string } & DocumentGridModel
    onSubmit: (newStatus: number, paymentDate: Date) => void
}) => {
    return (
        <>
            <GridMenuActionLink
                icon={faStream}
                onClick={() => setInvoiceStatusModalVisible(row.id)}
                translationKey="Invoice.Side.changeStatus"
            ></GridMenuActionLink>
            <Modal isOpen={invoiceStatusModalVisible === row.id}>
                <ChangeStatusModal
                    mapping="ReceiptStatus"
                    currentStatus={row.status}
                    paymentDate={row.paymentDate}
                    onSubmit={onSubmit}
                    pageTitleKey="Invoice.ChangeStatus.pageTitle"
                    close={() => setInvoiceStatusModalVisible(null)}
                    options={[
                        { value: 0, translationKey: 'Allaround.Status.draft' },
                        { value: 1, translationKey: 'Allaround.Status.invoiceSent' },
                        { value: 3, translationKey: 'Allaround.Status.invoicePaid' },
                    ]}
                    showPaymentDate={status => status === ReceiptStatus.Paid}
                />
            </Modal>
        </>
    )
}

const EInvoiceAction = ({
    row,
    isFiscalizationActive,
}: {
    row: { invoiceNumber: string } & DocumentGridModel
    isFiscalizationActive: boolean
}) => {
    const { t } = useTranslation()
    const [eInvoiceModalType, setEInvoiceModalType] = useState<EInvoiceModalType | null>(null)
    const [eInvoiceData, setEInvoiceData] = useState<EInvoiceModel | null>(null)
    const [hasTaxFreeItems, setHasTaxFreeItems] = useState<boolean>(false)

    const showEInvoiceModal = async () => {
        if (row.isFiscalized) {
            toastWarning('Invoice.SendEInvoice.Message.cannotSendFiscalized')
            return
        }

        let latestConfiguration: InvoiceConfigurationModel
        let einvoice: EInvoiceModel
        let invoiceDocument: any

        try {
            ;[latestConfiguration, einvoice, invoiceDocument] = await Promise.all([
                InvoiceApi.getConfiguration(String(row.id)),
                InvoiceApi.getEInvoiceData(row.id, row.clientId || undefined),
                InvoiceApi.get(row.id),
            ])
        } catch (e) {
            console.error('Failed to load configuration or e-invoice data for e-invoice', e)
            return
        }

        if (!latestConfiguration.isIbanValid) {
            toastWarning('Invoice.SendEInvoice.Message.warningInvalidIban')
        }
        const ok = validateEInvoicePreOpenForList({ missingEInvoiceData: latestConfiguration.missingEInvoiceData }, row, t)
        if (!ok) return

        if (!validateEInvoiceItems(invoiceDocument.items, t)) return

        const missingEInvoiceData = latestConfiguration.missingEInvoiceData

        const missingKeys: string[] = []

        if (missingEInvoiceData.companyOib) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.taxID')
        if (missingEInvoiceData.contactEmail) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.email')
        if (missingEInvoiceData.street) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.street')
        if (missingEInvoiceData.streetNumber) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.streetNumber')
        if (missingEInvoiceData.city) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.city')
        if (missingEInvoiceData.postalCode) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.zipCode')
        if (missingEInvoiceData.country) missingKeys.push('Invoice.SendEInvoice.Message.MissingData.country')

        if (missingKeys.length) {
            let finalMessage: string = t(missingKeys[0])

            if (missingKeys.length > 1) {
                for (let i = 1; i < missingKeys.length; i++) {
                    if (i == missingKeys.length - 1) {
                        finalMessage += ` ${t('Invoice.SendEInvoice.Message.MissingData.lastConjuction')} ${t(missingKeys[i])}`
                    } else {
                        finalMessage += `, ${t(missingKeys[i])}`
                    }
                }
            }

            toastWarning('Settings.EInvoice.Messages.warningMissingCompanyData', undefined, undefined, {
                missingCompanyData: finalMessage,
            })

            return
        }

        if (!row.clientId) {
            toastWarning('Invoice.SendEInvoice.Message.noClient')

            return
        }

        const missingKeysForClient: string[] = []

        if (!row.oib) {
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.taxID')
        } else if (row.country === 'HR' && !validateOib(row.oib)) {
            // Only validate OIB format for Croatian clients
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.validtaxID')
        }
        if (!row.email && !latestConfiguration.isCroatianCompany)
            missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.email')
        if (!row.address) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.street')
        if (!row.city) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.city')
        if (!row.postalCode) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.zipCode')
        if (!row.country) missingKeysForClient.push('Invoice.SendEInvoice.Message.MissingData.country')

        if (missingKeysForClient.length) {
            let finalMessage: string = t(missingKeysForClient[0])

            if (missingKeysForClient.length > 1) {
                for (let i = 1; i < missingKeysForClient.length; i++) {
                    if (i == missingKeysForClient.length - 1) {
                        finalMessage += ` ${t('Invoice.SendEInvoice.Message.MissingData.lastConjuction')} ${t(missingKeysForClient[i])}`
                    } else {
                        finalMessage += `, ${t(missingKeysForClient[i])}`
                    }
                }
            }

            toastWarning('Invoice.SendEInvoice.Message.noClientData', undefined, undefined, {
                missingClientData: finalMessage,
            })

            return
        }

        const isAfterLawChange = isLawInEffect()

        if (isAfterLawChange) {
            // Validate classification codes based on e-invoice data
            const defaultClassificationCode = einvoice.defaultClassificationCode
            const isDefaultClassificationCodeEmpty = !defaultClassificationCode || !defaultClassificationCode.trim()
            const hasItemWithEmptyClassificationCode = einvoice.classificationCodeItems.some(
                rc => !rc.classificationCode || !rc.classificationCode.trim()
            )
            const isExemptProcess =
                einvoice.processIdentificator === 'P4' || einvoice.processIdentificator === 'P9' || einvoice.processIdentificator === 'P10'

            if (isDefaultClassificationCodeEmpty && hasItemWithEmptyClassificationCode && !isExemptProcess) {
                const itemNames = einvoice.classificationCodeItems
                    .filter(rc => !rc.classificationCode || !rc.classificationCode.trim())
                    .map(rc => rc.name)
                    .join(', ')

                toastWarning('Allaround.Validation.Fiscalization.eInvoiceWrongClassificationCodeIntroBeforeModal', undefined, undefined, {
                    itemNames,
                })

                return
            }
        }

        setEInvoiceData(einvoice)
        const items = invoiceDocument.items || []
        setHasTaxFreeItems(items.some((i: any) => i.taxFree))

        setEInvoiceModalType(latestConfiguration.isEInvoiceConfigured ? EInvoiceModalType.SendEinvoice : EInvoiceModalType.DownloadEInvoice)
    }

    const requestEInvoice = (data: EInvoiceRequestModel) => {
        return InvoiceApi.requestEInvoice(data).then(result => {
            if (result.status === RequestEInvoiceResult.Error) {
                toastError(result.data, false, result.details)

                return
            }

            if (eInvoiceModalType === EInvoiceModalType.DownloadEInvoice || data.downloadAfterSend) {
                openDocument(`eRačun ${row.invoiceNumber}.xml`, result.data, 'application/xml')
            } else {
                toastSuccess('Invoice.SendEInvoice.Message.success')
            }

            setEInvoiceModalType(null)
        })
    }

    return (
        <>
            <GridMenuActionLink
                icon={faPaperPlane}
                onClick={showEInvoiceModal}
                translationKey="Invoice.Header.Menu.sendEInvoice"
            ></GridMenuActionLink>

            {eInvoiceModalType != null && eInvoiceData && (
                <Modal isOpen={eInvoiceModalType != null}>
                    <EInvoiceModal
                        details={{
                            forCharge: row.sumOfItemsFullPrice,
                            clientEmail: row.clientEmail,
                            creator: row.issuedBy,
                            clientName: row.clientName,
                            currency: row.currency,
                            documentNumber: row.invoiceNumber,
                            invoiceId: row.id,
                            officialDate: row.officialDate,
                        }}
                        eInvoiceData={eInvoiceData}
                        hasTaxFreeItems={hasTaxFreeItems}
                        close={() => setEInvoiceModalType(null)}
                        onSubmit={res => requestEInvoice({ ...res })}
                        type={eInvoiceModalType}
                    ></EInvoiceModal>
                </Modal>
            )}
        </>
    )
}

const InvoiceStatusAction = ({ row }: { row: DocumentGridModel }) => {
    const [isLogModalVisible, setIsLogModalVisible] = useState(false)

    return (
        <>
            <GridMenuActionLink
                icon={faHistory}
                onClick={() => setIsLogModalVisible(true)}
                translationKey="Invoice.Header.Menu.log"
            ></GridMenuActionLink>
            <Modal isOpen={isLogModalVisible}>
                <InvoiceLogModal invoiceId={row.id} close={() => setIsLogModalVisible(false)} />
            </Modal>
        </>
    )
}

export const InvoiceList = (props: InvoiceListConfig) => {
    const { t } = useOfferionTranslation()
    const navigate = useNavigate()
    const [defaultMailText, setDefaultMailText] = useState('')
    const [isFiscalizationActive, setIsFiscalizationActive] = useState(false)
    const [isEInvoiceConfigured, setIsEInvoiceConfigured] = useState(false)
    const [settingsLoaded, setSettingsLoaded] = useState(false)
    const offerionModal = useOfferionModal()

    // can be null or invoice id
    const [invoiceStatusModalVisible, setInvoiceStatusModalVisible] = useState<number | null>(null)

    useEffect(() => {
        CompanySettingsApi.getReceiptListSettings().then(result => {
            setIsFiscalizationActive(result.isFiscalizationActive)
            setDefaultMailText(result.defaultMailText)
            setIsEInvoiceConfigured(result.isEInvoiceConfigured)
            setSettingsLoaded(true)
        })
    }, [])

    const deleteInvoice = (invoiceId: number, invoiceNumber: string, gridActions: GridActions) => {
        offerionModal.open(
            'Invoice.DeleteInvoice.pageTitle',
            'Invoice.DeleteInvoice.questionYesNo',
            async () => {
                await InvoiceApi.delete(invoiceId)

                gridActions.refresh()

                toastSuccess('Invoice.DeleteInvoice.Message.SuccessDelete')
            },
            { invoiceNumber }
        )
    }

    const cancelInvoice = (invoiceId: number, invoiceNumber: string) => {
        offerionModal.open(
            'Invoice.CancelInvoice.pageTitle',
            'Invoice.CancelInvoice.questionYesNo',
            async () => {
                const newInvoiceId = await InvoiceApi.cancel(invoiceId)

                navigate(`/invoice/${newInvoiceId}`)
            },
            { invoiceNumber }
        )
    }

    const getDocumentFinal = (row: { invoiceNumber: string } & DocumentGridModel, shouldFiscalize: boolean) => {
        return InvoiceApi.getDocument(row.id, shouldFiscalize).then((data: any) => {
            openDocument(`${t('Invoice.Print.invoice')} ${row.invoiceNumber}.pdf`, data)

            return false
        })
    }

    const getDocumentStep1 = async (row: { invoiceNumber: string } & DocumentGridModel) => {
        const isAccountPaymentMethod = row.paymentMethod === PaymentMethod.Account

        // If already fiscalized, fiscalization not active or e-invoice already sent, just print
        if (row.isFiscalized || !isFiscalizationActive || row.eInvoiceSent) {
            await getDocumentFinal(row, false)
            return {
                openModal: false,
                oibDataFound: null,
                initialShouldNotFiscalize: false,
                showNewFiscalizeMessage: false,
                numberFormatError: false,
            }
        }

        // Check invoice number format against registered sale places
        let numberFormatInvalid = false
        try {
            const numCheck = await FiscalizationApi.canFiscalizeByNumber(row.id)
            numberFormatInvalid = !numCheck.isValid
        } catch (e) {
            console.error('Failed to check invoice number for fiscalization', e)
        }

        // transactional flow:
        // if name contains "obrt", treat as company-like and auto-print without fiscalization
        // if legalFormShort present, auto-print without fiscalization
        // else (legalFormShort empty OR OIB not found), open modal and allow fiscalization
        if (isAccountPaymentMethod) {
            if (row.oib) {
                try {
                    const res = await ClientApi.getAutofill(row.oib)
                    const { legalFormEmpty } = getAutofillFlags(res)
                    const isObrt = isObrtName(row.clientName)
                    if (res.dataFound) {
                        if (isObrt) {
                            // Business override: 'obrt' in name => company-like for Account; do NOT offer (auto-print)
                            await getDocumentFinal(row, false)
                            return {
                                openModal: false,
                                oibDataFound: false,
                                initialShouldNotFiscalize: true,
                                showNewFiscalizeMessage: false,
                                numberFormatError: false,
                            }
                        }
                        if (!legalFormEmpty) {
                            // legalFormShort present, company-like, means auto-print without fiscalization
                            await getDocumentFinal(row, false)
                            return {
                                openModal: false,
                                oibDataFound: true,
                                initialShouldNotFiscalize: true,
                                showNewFiscalizeMessage: false,
                                numberFormatError: false,
                            }
                        }
                        // legalFormShort empty, open modal and allow fiscalization
                        return {
                            openModal: true,
                            oibDataFound: false,
                            initialShouldNotFiscalize: numberFormatInvalid,
                            showNewFiscalizeMessage: false,
                            numberFormatError: numberFormatInvalid,
                        }
                    }
                    // OIB not found, open modal and allow fiscalization
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: !numberFormatInvalid,
                        numberFormatError: numberFormatInvalid,
                    }
                } catch (e) {
                    console.error('Failed to check OIB for fiscalization (print/account)', e)
                    // on error, fall back to showing modal without defaulting to don't fiscalize
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }
            }
            // no OIB available, open modal
            return {
                openModal: true,
                oibDataFound: false,
                initialShouldNotFiscalize: numberFormatInvalid,
                showNewFiscalizeMessage: !numberFormatInvalid,
                numberFormatError: numberFormatInvalid,
            }
        }

        // non-transactional (cash/other) flow
        const hasOib = !!row.oib
        const isAfterLawChange = isLawInEffect()

        if (isAfterLawChange && hasOib) {
            try {
                const res = await ClientApi.getAutofill(row.oib)
                const { legalFormEmpty } = getAutofillFlags(res)
                const isObrt = isObrtName(row.clientName)

                if (isObrt) {
                    return {
                        openModal: true,
                        oibDataFound: !!res.dataFound,
                        initialShouldNotFiscalize: true,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }

                if (legalFormEmpty) {
                    return {
                        openModal: true,
                        oibDataFound: false,
                        initialShouldNotFiscalize: numberFormatInvalid,
                        showNewFiscalizeMessage: false,
                        numberFormatError: numberFormatInvalid,
                    }
                }

                return {
                    openModal: true,
                    oibDataFound: !!res.dataFound,
                    initialShouldNotFiscalize: numberFormatInvalid || !!res.dataFound,
                    showNewFiscalizeMessage: false,
                    numberFormatError: numberFormatInvalid,
                }
            } catch (e) {
                console.error('Failed to check OIB for fiscalization (print/cash)', e)
                return {
                    openModal: true,
                    oibDataFound: false,
                    initialShouldNotFiscalize: numberFormatInvalid,
                    showNewFiscalizeMessage: false,
                    numberFormatError: numberFormatInvalid,
                }
            }
        }

        return {
            openModal: true,
            oibDataFound: false,
            initialShouldNotFiscalize: numberFormatInvalid,
            showNewFiscalizeMessage: false,
            numberFormatError: numberFormatInvalid,
        }
    }

    const changeStatus = async (row: DocumentGridModel, newStatus: number, paymentDate: Date, gridActions: GridActions) => {
        await InvoiceApi.changeStatus(row.id, newStatus, paymentDate)
        setInvoiceStatusModalVisible(null)
        gridActions.refresh()
    }

    const emailInvoice = (
        invoiceId: number,
        info: { toEmail: string; toEmailsCc: string; emailBody: string; includeCopyToSelf: boolean; shouldFiscalize: boolean }
    ) => {
        return InvoiceApi.sendInvoice(
            invoiceId,
            info.toEmail,
            info.toEmailsCc,
            info.emailBody,
            info.includeCopyToSelf,
            info.shouldFiscalize
        )
    }

    const columns = [
        {
            key: 'Name',
            labelKey: 'Invoices.Table.Header.invoiceNumber',
            cssClass: 'mobile-hide',
        },
    ]

    function invoiceStatusModalVisibleHandler(e: React.MouseEvent<HTMLButtonElement>, row: DocumentGridModel) {
        // if the click event propagates it'll open the invoice
        e.stopPropagation()
        setInvoiceStatusModalVisible(row.id)
    }

    return (
        <DocumentListContainer
            defaultFilter={[{ property: 'OfficialDate.Year', operator: 'Equal', value: new Date().getFullYear() }]}
            settingsLoaded={settingsLoaded}
            columns={columns}
            emptyMessageKey="Invoices.Table.emptyMessage"
            emptySearchMessageKey="Invoices.Table.emptySearchMessage"
            pageTitleKey={props.pageTitleKey}
            gridTitleKey={props.gridTitleKey}
            defaultCustomFilter={props.defaultCustomFilter}
            showMultiLineRowContent
            filtersComponent={
                props.hideFilters
                    ? null
                    : ({ currentCustomFilter, updateFilter }) => {
                          return (
                              <GridFilterForm
                                  titleKey="Invoices.Side.searchTitle"
                                  searchButtonLabelKey="Invoices.Side.buttonSearch"
                                  onSubmit={values => {
                                      const filters = []

                                      if (values.clientName)
                                          filters.push({ property: 'Client.Name', operator: 'Contains', value: values.clientName })

                                      if (values.number) filters.push({ property: 'Number', operator: 'Contains', value: values.number })

                                      if (values.day) filters.push({ property: 'OfficialDate.Day', operator: 'Equal', value: values.day })

                                      if (values.month)
                                          filters.push({ property: 'OfficialDate.Month', operator: 'Equal', value: values.month })

                                      if (values.year)
                                          filters.push({ property: 'OfficialDate.Year', operator: 'Equal', value: values.year })

                                      if (values.status !== '')
                                          filters.push({ property: 'Status', operator: 'Equal', value: values.status })

                                      if (values.paymentMethod)
                                          filters.push({ property: 'PaymentMethod', operator: 'Equal', value: values.paymentMethod })

                                      updateFilter(filters, values.customFilter)
                                  }}
                                  initialValues={{
                                      clientName: '',
                                      day: '',
                                      month: '',
                                      year: new Date().getFullYear(),
                                      number: '',
                                      status: '',
                                      paymentMethod: '',
                                      customFilter: currentCustomFilter || '',
                                  }}
                              >
                                  <GridFilterInput name="clientName" placeholderKey="Invoices.Side.clientPlaceholder"></GridFilterInput>
                                  <GridFilterInput name="number" placeholderKey="Invoices.Side.invoiceNumberPlaceholder"></GridFilterInput>
                                  <DocumentDateFilters />

                                  <div className="select-container search__item mod-margin-bottom-8">
                                      <Field as="select" name="status" className="select">
                                          <option value="">{t('Invoices.Side.statusAll')}</option>
                                          <option value="0">{t('Allaround.Status.draft')}</option>
                                          <option value="1">{t('Allaround.Status.invoiceSent')}</option>
                                          <option value="3">{t('Allaround.Status.invoicePaid')}</option>
                                          <option value="4">{t('Allaround.Status.invoiceCancelled')}</option>
                                      </Field>
                                  </div>

                                  <div className="select-container search__item mod-margin-bottom-8">
                                      <Field as="select" name="customFilter" className="select">
                                          <option value="">
                                              {t(
                                                  isFiscalizationActive
                                                      ? 'Invoices.Side.paymentAndFiscalization'
                                                      : 'Invoices.Side.paymentOnly'
                                              )}
                                          </option>
                                          {isFiscalizationActive && (
                                              <option value="OnlyNotFiscalized">{t('Invoices.Side.fiscalizationNotDone')}</option>
                                          )}
                                          <option value="PastOrDue">{t('Allaround.Status.invoicePastDue')}</option>
                                      </Field>
                                  </div>

                                  <div className="select-container search__item mod-margin-bottom-8">
                                      <Field as="select" name="paymentMethod" className="select">
                                          <option value="">{t('Invoices.Side.paymentTypeAll')}</option>
                                          <option value="0">{t('Allaround.PaymentType.cash.title')}</option>
                                          <option value="1">{t('Allaround.PaymentType.creditCard.title')}</option>
                                          <option value="2">{t('Allaround.PaymentType.bankAccount.title')}</option>
                                          <option value="3">{t('Allaround.PaymentType.check.title')}</option>
                                          <option value="4">{t('Allaround.PaymentType.other.title')}</option>
                                      </Field>
                                  </div>
                              </GridFilterForm>
                          )
                      }
            }
            hidePaging={props.hidePaging}
            customRowClass={row => (shouldFiscalize(row, isFiscalizationActive) ? 'row-color--orange' : '')}
            buttonsComponent={React.createElement(props.buttons)}
            menuCellComponent={({ row, gridActions }) => {
                return (
                    <GridMenuCell>
                        <GridMenuLink icon={faEdit} to={`/invoice/${row.id}`} translationKey="Invoices.Table.ItemMenu.edit"></GridMenuLink>
                        <PrintAction action={() => getDocumentStep1(row)} row={row} getDocumentFinal={getDocumentFinal} />
                        <EmailAction
                            row={row}
                            defaultMailText={defaultMailText}
                            isFiscalizationActive={isFiscalizationActive}
                            emailInvoice={emailInvoice}
                        />
                        {!row.cancelledFromReceiptId && !row.isCancelled && (
                            <GridMenuActionLink
                                icon={faTimes}
                                onClick={() => cancelInvoice(row.id, row.invoiceNumber)}
                                translationKey="Invoice.Side.cancelInvoice"
                            ></GridMenuActionLink>
                        )}
                        <EInvoiceAction row={row} isFiscalizationActive={isFiscalizationActive} />
                        {row.status !== ReceiptStatus.Cancelled && (
                            <ChangeStatusAction
                                invoiceStatusModalVisible={invoiceStatusModalVisible}
                                setInvoiceStatusModalVisible={setInvoiceStatusModalVisible}
                                row={row}
                                onSubmit={(newStatus, paymentDate) => changeStatus(row, newStatus, paymentDate, gridActions)}
                            />
                        )}
                        <InvoiceStatusAction row={row} />
                        {!row.isCancelled && !row.isFiscalized && (
                            <GridMenuActionLink
                                icon={faTrashAlt}
                                onClick={() => deleteInvoice(row.id, row.invoiceNumber, gridActions)}
                                translationKey="Invoices.Table.ItemMenu.delete"
                            ></GridMenuActionLink>
                        )}
                    </GridMenuCell>
                )
            }}
            defaultPageSize={props.defaultPageSize}
            orderByProperty="OfficialDate"
            projectionName="ReceiptsGridModel"
            firstColumnSelector={(row: { invoiceNumber: string } & DocumentGridModel) => {
                return (
                    <>
                        <span
                            className="table__cell table__cell__title mod-cursor-pointer"
                            data-by={row.issuedBy}
                            onClick={() => navigate(`/invoice/${row.id}`)}
                        >
                            <span className="table__cell__status">
                                <StatusIndicator
                                    small
                                    mapping="ReceiptStatus"
                                    status={row.status}
                                    onClick={e => invoiceStatusModalVisibleHandler(e, row)}
                                />
                            </span>
                            {row.invoiceNumber}
                        </span>
                        <span
                            className="table__cell table__cell__author table__cell--prefixed mobile-only"
                            data-prefix={t('Invoices.Table.Mobile.created')}
                        >
                            {row.issuedBy}
                        </span>
                    </>
                )
            }}
        />
    )
}

export const InvoiceListContainer = () => {
    const { t } = useOfferionTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const createInvoice = async () => {
        const result = await InvoiceApi.createForClient(null)

        if (result.id) {
            navigate(`/invoice/${result.id}`, { state: { createFlow: true } })
        } else {
            const message = t('Allaround.Message.packageLimitReached', {
                offersOrInvoicesGenitiv: t('Allaround.Message.invoicesGenitiv'),
            })

            toastWarning(message, true)
        }
    }

    return (
        <>
            <BreadcrumbsItem to="/invoices">{t('Invoices.Header.pageTitle')}</BreadcrumbsItem>

            <InvoiceList
                defaultCustomFilter={new URLSearchParams(location.search).get('custom-filter')}
                pageTitleKey={'Invoices.Header.pageTitle'}
                gridTitleKey="Invoices.Table.Header.title"
                buttons={() => {
                    return (
                        <>
                            <div className="button-container">
                                <button className="button button--white table__button" onClick={createInvoice}>
                                    <span className="icon icon__plus"></span>
                                </button>
                                <span className="button-text table__button-text mod-margin-left-4">{t('Invoices.Header.newInvoice')}</span>
                            </div>

                            <AggregatedResultsButton
                                aggregatedResultsPromiseFactory={config =>
                                    InvoiceApi.getListAggregatedValues('ReceiptsGridModel', config.filter, config.customFilter)
                                }
                            />
                        </>
                    )
                }}
            />
        </>
    )
}
